import React, { FC, useMemo } from 'react'
import { Modal } from 'react-bootstrap'
import { SubscriptionTimeline, TimelineEvent } from 'src/app/_interfaces/customer.interface'
import { SubscriptionTimelineOrder } from 'src/redux/actions/customerAction'

// ─── Color tokens ─────────────────────────────────────────────────────────────

const COLOR_MAP: Record<string, { dot: string; ring: string; badge: string; badgeBg: string }> = {
  blue:  { dot: '#3b82f6', ring: '#bfdbfe', badge: '#1d4ed8', badgeBg: 'rgba(59,130,246,0.10)' },
  green: { dot: '#10b981', ring: '#a7f3d0', badge: '#065f46', badgeBg: 'rgba(16,185,129,0.10)' },
  red:   { dot: '#ef4444', ring: '#fecaca', badge: '#991b1b', badgeBg: 'rgba(239,68,68,0.10)' },
  amber: { dot: '#f59e0b', ring: '#fde68a', badge: '#92400e', badgeBg: 'rgba(245,158,11,0.10)' },
  gray:  { dot: '#6b7280', ring: '#d1d5db', badge: '#374151', badgeBg: 'rgba(107,114,128,0.10)' },
}

const getColors = (color: string) => COLOR_MAP[color] ?? COLOR_MAP.gray

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatDate = (iso: string): string =>
  new Date(iso).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  })

const formatDateShort = (iso: string): string =>
  new Date(iso).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })

const localTimezone = (): string => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone } catch { return 'local time' }
}

const formatAmount = (amount: number, currency = 'USD'): string => {
  const symbol = currency === 'USD' ? '$' : `${currency} `
  return `${symbol}${Math.abs(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

const humanizeFrequency = (freq: string): string => {
  const map: Record<string, string> = {
    EVERY_30_DAYS: 'Monthly',
    EVERY_365_DAYS: 'Yearly',
    EVERY_7_DAYS: 'Weekly',
    FREE_FOREVER: 'Free Forever',
  }
  return map[freq] ?? freq.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const MrrBadge: FC<{ impact: number; currency?: string }> = ({ impact, currency }) => {
  if (!impact) return null
  const pos = impact > 0
  return (
    <span style={{
      fontWeight: 700,
      fontSize: '12px',
      color: pos ? '#059669' : '#dc2626',
      background: pos ? 'rgba(5,150,105,0.10)' : 'rgba(220,38,38,0.10)',
      borderRadius: '5px',
      padding: '2px 8px',
      letterSpacing: '0.01em',
    }}>
      {pos ? '+' : '−'}{formatAmount(impact, currency)}
    </span>
  )
}

const Dot: FC<{ color: string }> = ({ color }) => {
  const c = getColors(color)
  return (
    <div style={{
      position: 'absolute',
      left: '-9px',
      top: '3px',
      width: '16px',
      height: '16px',
      borderRadius: '50%',
      background: c.dot,
      border: `2.5px solid #fff`,
      boxShadow: `0 0 0 2.5px ${c.ring}`,
      zIndex: 1,
      flexShrink: 0,
    }} />
  )
}

// ─── Timeline item ────────────────────────────────────────────────────────────

const TimelineItem: FC<{ event: TimelineEvent }> = ({ event }) => {
  const { metadata, color, created_at, message, gateway } = event
  const c = getColors(color)
  const mrrImpact = metadata?.mrr_impact
  const currency = metadata?.currency

  // Level 3 chips — gateway + all metadata fields
  const chips: Array<{ label: string; value: string }> = []
  if (gateway) chips.push({ label: 'Gateway', value: gateway })
  if (metadata?.plan_name) chips.push({ label: 'Plan', value: metadata.plan_name })
  if (metadata?.previous_plan_name) chips.push({ label: 'From', value: metadata.previous_plan_name })
  if (metadata?.amount !== undefined && metadata?.amount !== null && metadata.amount > 0) {
    chips.push({ label: 'Amount', value: formatAmount(metadata.amount, currency) })
  }
  if (metadata?.subscription_id) chips.push({ label: 'Sub ID', value: metadata.subscription_id })
  if (metadata?.billing_frequency) chips.push({ label: 'Billing', value: humanizeFrequency(metadata.billing_frequency) })
  if (metadata?.source) chips.push({ label: 'Source', value: metadata.source })

  const hasLevel3 = chips.length > 0 || (mrrImpact !== undefined && mrrImpact !== null && mrrImpact !== 0)

  return (
    <div style={{ position: 'relative', paddingBottom: '28px' }}>
      <Dot color={color} />

      {/* Card */}
      <div style={{
        background: '#fff',
        border: '1px solid #e9ecef',
        borderRadius: '10px',
        padding: '14px 16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        marginLeft: '16px',
      }}>

        {/* ── Level 1: badge + title + timestamp ── */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '10px', marginBottom: '6px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', minWidth: 0 }}>
            {/* Event type badge */}
            <span style={{
              fontSize: '10px',
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: c.badge,
              background: c.badgeBg,
              borderRadius: '4px',
              padding: '2px 7px',
              whiteSpace: 'nowrap',
            }}>
              {event.event.replace(/_/g, ' ')}
            </span>
            {/* Title */}
            <span style={{ fontSize: '13.5px', fontWeight: 700, color: '#111827', lineHeight: 1.35 }}>
              {metadata?.title ?? event.event}
            </span>
          </div>
          {/* Timestamp */}
          <div style={{
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: '1px',
          }}>
            <span style={{ fontSize: '11.5px', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>
              {new Date(created_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
            </span>
            <span style={{ fontSize: '10.5px', color: '#9ca3af', whiteSpace: 'nowrap' }}>
              {new Date(created_at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: true })}
            </span>
          </div>
        </div>

        {/* ── Level 2: description message ── */}
        <p style={{ margin: '0 0 0 0', fontSize: '12.5px', color: '#6b7280', lineHeight: 1.55 }}>
          {message}
        </p>

        {/* ── Level 3: metadata chips + MRR ── */}
        {hasLevel3 && (
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '6px 14px',
            marginTop: '10px',
            paddingTop: '10px',
            borderTop: '1px solid #f3f4f6',
            alignItems: 'center',
          }}>
            {chips.map(({ label, value }) => (
              <span key={label} style={{ fontSize: '11.5px', color: '#6b7280', display: 'inline-flex', gap: '4px' }}>
                <span style={{ fontWeight: 600, color: '#374151' }}>{label}:</span>
                <span>{value}</span>
              </span>
            ))}
            {mrrImpact !== undefined && mrrImpact !== null && mrrImpact !== 0 && (
              <span style={{ fontSize: '11.5px', color: '#6b7280', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ fontWeight: 600, color: '#374151' }}>MRR Impact:</span>
                <MrrBadge impact={mrrImpact} currency={currency} />
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Empty state ──────────────────────────────────────────────────────────────

const EmptyTimeline: FC = () => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '56px 24px', gap: '14px' }}>
    <svg width='52' height='52' fill='none' viewBox='0 0 24 24' stroke='#d1d5db' strokeWidth='1.4'>
      <path strokeLinecap='round' strokeLinejoin='round' d='M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25z' />
    </svg>
    <div style={{ textAlign: 'center' }}>
      <p style={{ margin: 0, fontSize: '15px', fontWeight: 700, color: '#374151' }}>No billing history found</p>
      <p style={{ margin: '5px 0 0', fontSize: '13px', color: '#9ca3af' }}>This customer has not had any subscription activity yet.</p>
    </div>
  </div>
)

// ─── Skeleton ─────────────────────────────────────────────────────────────────

const Skeleton: FC = () => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
    {[100, 75, 90].map((w, i) => (
      <div key={i} style={{ background: '#fff', border: '1px solid #e9ecef', borderRadius: '10px', padding: '14px 16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <div style={{ height: '18px', width: '64px', borderRadius: '4px', background: '#f3f4f6' }} />
            <div style={{ height: '14px', width: `${w + 40}px`, borderRadius: '4px', background: '#e9ecef' }} />
          </div>
          <div style={{ height: '12px', width: '80px', borderRadius: '4px', background: '#f3f4f6' }} />
        </div>
        <div style={{ height: '12px', width: `${w + 60}%`, borderRadius: '4px', background: '#f3f4f6' }} />
        <div style={{ display: 'flex', gap: '12px', marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #f3f4f6' }}>
          <div style={{ height: '11px', width: '90px', borderRadius: '4px', background: '#f3f4f6' }} />
          <div style={{ height: '11px', width: '70px', borderRadius: '4px', background: '#f3f4f6' }} />
          <div style={{ height: '11px', width: '60px', borderRadius: '4px', background: '#f3f4f6' }} />
        </div>
      </div>
    ))}
  </div>
)

// ─── Header summary ───────────────────────────────────────────────────────────

const HeaderSummary: FC<{ data: SubscriptionTimeline }> = ({ data }) => {
  const { user, current_mrr } = data
  const mrrColor = current_mrr > 0 ? '#059669' : current_mrr < 0 ? '#dc2626' : '#6b7280'

  return (
    <div style={{
      background: 'linear-gradient(135deg, #f8faff 0%, #f0f4ff 100%)',
      border: '1px solid #dde5f4',
      borderRadius: '12px',
      padding: '16px 20px',
      marginBottom: '20px',
      display: 'flex',
      flexWrap: 'wrap',
      gap: '14px',
      alignItems: 'center',
      justifyContent: 'space-between',
    }}>
      {/* Left: customer identity */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
        {/* Avatar circle */}
        <div style={{
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          fontWeight: 700,
          fontSize: '16px',
          flexShrink: 0,
          textTransform: 'uppercase',
        }}>
          {(user.name || user.email || '?')[0].toUpperCase()}
        </div>
        <div style={{ minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {user.name || '—'}
          </p>
          <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#6b7280' }}>{user.email}</p>
          {user.date_joined && (
            <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#9ca3af' }}>
              Joined {formatDateShort(user.date_joined)}
            </p>
          )}
        </div>
      </div>

      {/* Right: plan + MRR stats */}
      <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
        {user.current_plan && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            <span style={{ fontSize: '10px', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '3px' }}>
              Current Plan
            </span>
            <span style={{
              fontSize: '12.5px',
              fontWeight: 700,
              color: '#1e40af',
              background: 'rgba(30,64,175,0.08)',
              borderRadius: '5px',
              padding: '2px 8px',
            }}>
              {user.current_plan}
            </span>
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          <span style={{ fontSize: '10px', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '3px' }}>
            Current MRR
          </span>
          <span style={{ fontSize: '17px', fontWeight: 800, color: mrrColor, letterSpacing: '-0.01em' }}>
            ${Number(current_mrr).toFixed(2)}
          </span>
        </div>
      </div>
    </div>
  )
}

// ─── Sort toggle ──────────────────────────────────────────────────────────────

const TimelineOrderToggle: FC<{
  order: SubscriptionTimelineOrder
  loading: boolean
  onOrderChange: (order: SubscriptionTimelineOrder) => void
}> = ({ order, loading, onOrderChange }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginBottom: '16px', gap: '8px' }}>
    <span style={{ fontSize: '11px', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
      Sort by
    </span>
    <div
      style={{
        display: 'inline-flex',
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        padding: '2px',
        gap: '2px',
      }}
    >
      <button
        type='button'
        disabled={loading}
        onClick={() => onOrderChange('desc')}
        style={{
          border: 'none',
          borderRadius: '6px',
          padding: '5px 12px',
          fontSize: '12px',
          fontWeight: 600,
          cursor: loading ? 'not-allowed' : 'pointer',
          background: order === 'desc' ? '#6366f1' : 'transparent',
          color: order === 'desc' ? '#fff' : '#6b7280',
          opacity: loading ? 0.6 : 1,
        }}
      >
        Newest first
      </button>
      <button
        type='button'
        disabled={loading}
        onClick={() => onOrderChange('asc')}
        style={{
          border: 'none',
          borderRadius: '6px',
          padding: '5px 12px',
          fontSize: '12px',
          fontWeight: 600,
          cursor: loading ? 'not-allowed' : 'pointer',
          background: order === 'asc' ? '#6366f1' : 'transparent',
          color: order === 'asc' ? '#fff' : '#6b7280',
          opacity: loading ? 0.6 : 1,
        }}
      >
        Oldest first
      </button>
    </div>
  </div>
)

// ─── Main modal ───────────────────────────────────────────────────────────────

type Props = {
  show: boolean
  customerName: string | null
  loading: boolean
  data: SubscriptionTimeline | null
  order: SubscriptionTimelineOrder
  onOrderChange: (order: SubscriptionTimelineOrder) => void
  onClose: () => void
}

const CustomerBillingTimeline: FC<Props> = ({
  show,
  customerName,
  loading,
  data,
  order,
  onOrderChange,
  onClose,
}) => {
  const timelineEvents = useMemo(() => {
    if (!data?.timeline?.length) return []
    return data.timeline
  }, [data])

  return (
    <Modal show={show} onHide={onClose} size='lg' scrollable centered dialogClassName='billing-timeline-modal'>
      {/* Header */}
      <Modal.Header closeButton style={{ borderBottom: '1px solid #f0f0f5', padding: '16px 22px', background: '#fff' }}>
        <Modal.Title style={{ fontSize: '15.5px', fontWeight: 700, color: '#111827', display: 'flex', alignItems: 'center', gap: '9px' }}>
          <span style={{
            width: '32px', height: '32px', borderRadius: '8px',
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <svg width='16' height='16' fill='none' viewBox='0 0 24 24' stroke='#fff' strokeWidth='2.2'>
              <path strokeLinecap='round' strokeLinejoin='round' d='M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z' />
            </svg>
          </span>
          {customerName ? `${customerName} — Billing History` : 'Billing History'}
        </Modal.Title>
      </Modal.Header>

      {/* Body */}
      <Modal.Body style={{ padding: '20px 22px', background: '#f8f9fb', minHeight: '320px' }}>
        {loading ? (
          <Skeleton />
        ) : !data ? (
          <EmptyTimeline />
        ) : (
          <>
            <HeaderSummary data={data} />

            <TimelineOrderToggle order={order} loading={loading} onOrderChange={onOrderChange} />

            {timelineEvents.length === 0 ? (
              <EmptyTimeline />
            ) : (
              <div style={{
                position: 'relative',
                paddingLeft: '20px',
                borderLeft: '2px solid #e2e8f0',
                marginLeft: '8px',
              }}>
                {timelineEvents.map((event) => (
                  <TimelineItem key={event.id} event={event} />
                ))}
              </div>
            )}
          </>
        )}
      </Modal.Body>

      {/* Footer */}
      <Modal.Footer style={{
        borderTop: '1px solid #f0f0f5',
        padding: '10px 22px',
        background: '#fff',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '8px',
      }}>
        <span style={{ fontSize: '11px', color: '#9ca3af', display: 'flex', alignItems: 'center', gap: '5px' }}>
          <svg width='12' height='12' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth='2'>
            <path strokeLinecap='round' strokeLinejoin='round' d='M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z' />
          </svg>
          Times shown in {localTimezone()}
        </span>
        <button type='button' className='btn btn-sm btn-light fw-semibold px-4' onClick={onClose}>
          Close
        </button>
      </Modal.Footer>
    </Modal>
  )
}

export default CustomerBillingTimeline
